import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DocumentModifiersContext, DocumentValuesContext } from 'contexts';
import { cfiLogic } from 'utilities';

/**
 * This provider exists to reflect context from the active Document component to the rest of the app.
 *
 * @param {object} properties
 * @param {object} properties.children
 * @param {boolean} properties.debug
 *  there may be multiple instances of DocumentProvider, where additional instances are used to trap context for inactive Document components
 *  the debug property can be used to expose (in the window.v7 variable) the context for the Document we care about (likely the active Document)
 */
export const DocumentProvider = ({ children, debug = false }) => {
  const [cfi, setCfi] = useState();
  const cfiCache = useRef();
  const [percentageScrolled, setPercentageScrolled] = useState();
  const [state, setState] = useState();
  const [frame, setFrame] = useState();
  const [frameDocument, setFrameDocument] = useState();
  const [frameContainer, setFrameContainer] = useState();
  const [isReadyToDisplay, setIsReadyToDisplay] = useState(false);

  // The frame container should not change so long as the frame is the same element.
  useEffect(
    function onFrameChange() {
      setFrameContainer(frame?.parentElement?.parentElement);
    },
    [frame],
  );

  // A change in the state indicates that the frame may have a new contentDocument.
  useEffect(
    function onFrameStateChange() {
      setFrameDocument(frame?.contentDocument);
    },
    [frame, state],
  );

  // Clear the CFI cache whenever the document frame changes.
  useEffect(
    function resetCFICache() {
      cfiCache.current = {
        cfis: {}, // CFIs mapping to elements, ranges, and snippets
        elements: new WeakMap(), // elements mapping to CFIs
      };
    },
    [frameDocument],
  );

  const getCfiForElement = useCallback((element) => {
    if (!(element instanceof Element)) {
      return;
    }

    // Try to get value from cache
    let cfi = cfiCache.current.elements.get(element);

    if (typeof cfi === 'string') {
      // Return cached value
      return cfi;
    }

    // Compute value
    cfi = cfiLogic.getCfiForElement(element);

    // Add to cache
    cfiCache.current.elements.set(element, cfi);
    (cfiCache.current.cfis[cfi] ??= {}).element = element;

    // Return computed value
    return cfi;
  }, []);

  const getElementForCfi = useCallback(
    (cfi) => {
      if (typeof cfi !== 'string') {
        return;
      }

      // Try to get value from cache
      let element = cfiCache.current.cfis[cfi]?.element;

      if (element instanceof Element) {
        // Return cached value
        return element;
      }

      // Compute value
      element = cfiLogic.getElementForCfi(frameDocument, cfi);

      // Add to cache
      (cfiCache.current.cfis[cfi] ??= {}).element = element;
      cfiCache.current.elements.set(element, cfi);

      // Return computed value
      return element;
    },
    [frameDocument],
  );

  const getRangeForCfi = useCallback(
    (cfi) => {
      if (typeof cfi !== 'string') {
        return;
      }

      // Try to get value from cache
      let range = cfiCache.current.cfis[cfi]?.range;

      if (range) {
        // Return a clone of the cached value
        return range.cloneRange();
      }

      // Compute value
      range = cfiLogic.getRangeForCfi(frameDocument, cfi);

      // Add to cache
      (cfiCache.current.cfis[cfi] ??= {}).range = range;

      // Return a clone of the computed value
      // We must clone the range because otherwise modifications made to the object elsewhere will affect the cached value.
      return range.cloneRange();
    },
    [frameDocument],
  );

  const getSnippetForCfi = useCallback(
    (cfi) => {
      if (typeof cfi !== 'string') {
        return;
      }

      // Try to get value from cache
      let snippet = cfiCache.current.cfis[cfi]?.snippet;

      if (typeof snippet === 'string') {
        // Return cached value
        return snippet;
      }

      // Compute value
      const range = getRangeForCfi(cfi);
      snippet = cfiLogic.getSnippetForRange(range);

      // Add to cache
      (cfiCache.current.cfis[cfi] ??= {}).snippet = snippet;

      // Return computed value
      return snippet;
    },
    [getRangeForCfi],
  );

  const removeFrame = useCallback(() => setFrame(), []);

  const values = useMemo(
    () => ({
      cfi,
      percentageScrolled,
      state,
      frame,
      frameContainer,
      frameDocument,
      getCfiForElement,
      getElementForCfi,
      getRangeForCfi,
      getSnippetForCfi,
      isReadyToDisplay,
    }),
    [
      cfi,
      percentageScrolled,
      state,
      frame,
      frameContainer,
      frameDocument,
      getCfiForElement,
      getElementForCfi,
      getRangeForCfi,
      getSnippetForCfi,
      isReadyToDisplay,
    ],
  );

  const modifiers = useMemo(
    () => ({
      setCfi,
      setPercentageScrolled,
      setState,
      setFrame,
      setIsReadyToDisplay,
      removeFrame,
    }),
    [
      setCfi,
      setPercentageScrolled,
      setState,
      setFrame,
      removeFrame,
      setIsReadyToDisplay,
    ],
  );

  // For debug
  if (debug) {
    window.v7.document = {
      ...values,
      ...modifiers,
    };
  }

  return (
    <DocumentValuesContext.Provider value={values}>
      <DocumentModifiersContext.Provider value={modifiers}>
        {children}
      </DocumentModifiersContext.Provider>
    </DocumentValuesContext.Provider>
  );
};

export default DocumentProvider;
